import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { gql } from "@medbillai/generated-graphql-types";
import { errorHandling } from "../../../lib/apollo/utils";
import { useAuth } from "../../../lib/auth";
import { normalizeEobData } from "./useNormalizedEobs";
type FlaggedEobCount = {
  countFlaggedExplanationOfBenefits: number;
};
export const getFlaggedEobsQuery = gql(/* GraphQL */`
  query GetFlaggedEobs {
    queryFlaggedExplanationOfBenefits {
      id
      title
      status
      created
      resourceId
      totalsByType {
        memberLiability {
          value
          currency
        }
      }
      decision {
        text
        coding {
          code
        }
      }
      item {
        id
        productOrService {
          text
          coding {
            code
            display
          }
        }
        reviewOutcome {
          decision {
            text
          }
          reason {
            text
          }
        }
      }
      total {
        category {
          coding {
            code
          }
        }
        amount {
          currency
          value
        }
      }
      patient {
        display
      }
      billablePeriod
      provider {
        display
      }
      facility {
        display
      }
      serviceDate
      findings {
        id
        explanation
        userDismissed
        lastReviewStatus
        type
        note
      }
    }
  }
`);
export const getFlaggedEobsCountQuery = gql(/* GraphQL */`
  query GetFlaggedEobsCount {
    countFlaggedExplanationOfBenefits
  }
`);
export function useFlaggedEobs() {
  const {
    isAuthenticated,
    isReady
  } = useAuth();
  const {
    loading,
    data,
    error,
    refetch,
    networkStatus
  } = useQuery(getFlaggedEobsQuery, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    skip: !isAuthenticated || !isReady
  });
  const normalizedData = useMemo(() => {
    return normalizeEobData(data?.queryFlaggedExplanationOfBenefits || []);
  }, [data]);
  const totalCount = useMemo(() => normalizedData.length, [normalizedData]);
  return {
    loading,
    data: normalizedData,
    totalCount,
    networkStatus,
    refetch,
    error
  };
}
export function useFlaggedEobsCount() {
  const {
    isAuthenticated,
    isReady
  } = useAuth();
  const {
    loading,
    data,
    error,
    refetch
  } = useQuery<FlaggedEobCount>(getFlaggedEobsCountQuery, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated || !isReady
  });
  const count = useMemo(() => {
    return data?.countFlaggedExplanationOfBenefits ?? 0;
  }, [data]);
  return {
    loading,
    count,
    refetch,
    error
  };
}